import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  MessageSquare, 
  Save, 
  Globe, 
  Eye, 
  EyeOff,
  Copy,
  CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const PromptSettings: React.FC = () => {
  const [prompt, setPrompt] = useState(`You are a friendly assistant for a Tunisian salon Facebook page. Respond in Derja or French based on the user's language.

Key guidelines:
- Be warm and welcoming
- Use appropriate Tunisian dialect (Derja) or French
- Keep responses concise and helpful
- Provide accurate information about services
- Handle complaints professionally
- Ask for clarification when needed

User message: {message}

Response:`);
  
  const [language, setLanguage] = useState('derja');
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showCopied, setShowCopied] = useState(false);

  const languages = [
    { value: 'derja', label: 'Tunisian Arabic (Derja - Arabic Script)', description: 'Local Tunisian dialect with Arabic letters' },
    { value: 'derja_latin', label: 'Tunisian Arabic (Derja - Latin Script)', description: 'Local Tunisian dialect with Latin letters' },
    { value: 'french', label: 'French', description: 'Formal French language' },
    { value: 'arabic', label: 'Modern Standard Arabic', description: 'Classical Arabic' },
    { value: 'auto', label: 'Auto-detect', description: 'Detect user language automatically' }
  ];

  const handleSave = async () => {
    setIsSaving(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSaving(false);
    // Show success message
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(prompt);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  const previewMessage = "مرحبا، عندي موعد غدا الساعة 3؟";
  const previewResponse = "مرحبا! نعم، عندك موعد غدا الساعة 3. شكرا على التأكيد. نراك قريبا! 😊";

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl font-bold text-gray-900">Prompt Settings</h1>
        <p className="text-gray-600 mt-2">Configure your AI assistant's response style and language preferences.</p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Settings */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="lg:col-span-2 space-y-6"
        >
          {/* Language Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Globe className="h-5 w-5" />
                <span>Language Preference</span>
              </CardTitle>
              <CardDescription>
                Choose the primary language for AI responses
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger>
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((lang) => (
                    <SelectItem key={lang.value} value={lang.value}>
                      <div>
                        <div className="font-medium">{lang.label}</div>
                        <div className="text-sm text-gray-500">{lang.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Prompt Template */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <MessageSquare className="h-5 w-5" />
                <span>AI Prompt Template</span>
              </CardTitle>
              <CardDescription>
                Customize how your AI assistant responds to messages
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsPreviewMode(!isPreviewMode)}
                  >
                    {isPreviewMode ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                    {isPreviewMode ? 'Hide Preview' : 'Show Preview'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopy}
                  >
                    {showCopied ? <CheckCircle className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                    {showCopied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>
              
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Enter your AI prompt template..."
                className="min-h-[300px] font-mono text-sm"
              />
              
              <div className="text-sm text-gray-600">
                <p><strong>Available variables:</strong></p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li><code className="bg-gray-100 px-1 rounded">{"{message}"}</code> - User's message</li>
                  <li><code className="bg-gray-100 px-1 rounded">{"{page_name}"}</code> - Facebook page name</li>
                  <li><code className="bg-gray-100 px-1 rounded">{"{user_name}"}</code> - User's name (if available)</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full"
              size="lg"
            >
              {isSaving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {isSaving ? 'Saving...' : 'Save Settings'}
            </Button>
          </motion.div>
        </motion.div>

        {/* Preview and Tips */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="space-y-6"
        >
          {/* Preview */}
          {isPreviewMode && (
            <Card>
              <CardHeader>
                <CardTitle>Response Preview</CardTitle>
                <CardDescription>
                  See how your AI will respond
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">User message:</p>
                  <p className="text-sm font-medium">{previewMessage}</p>
                </div>
                <div className="bg-primary/10 p-3 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">AI response:</p>
                  <p className="text-sm font-medium">{previewResponse}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tips */}
          <Card>
            <CardHeader>
              <CardTitle>Tips for Better Prompts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-sm space-y-2">
                <p className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                  <span>Be specific about your business type and services</span>
                </p>
                <p className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                  <span>Include tone guidelines (friendly, professional, etc.)</span>
                </p>
                <p className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                  <span>Specify how to handle common questions</span>
                </p>
                <p className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                  <span>Set character limits for responses</span>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Language Info */}
          <Card>
            <CardHeader>
              <CardTitle>Language Support</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div>
                  <h4 className="font-medium text-gray-900">Derja (Arabic Script)</h4>
                  <p className="text-gray-600">Local dialect with Arabic letters, most authentic for Tunisian customers</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Derja (Latin Script)</h4>
                  <p className="text-gray-600">Local dialect with Latin letters, easier for mixed audiences</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">French</h4>
                  <p className="text-gray-600">Formal language, good for business communication</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Auto-detect</h4>
                  <p className="text-gray-600">AI chooses language based on user's message</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default PromptSettings; 